import { AlertTriangle, CheckCircle2, Link2, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import useSWR from "swr";
import type { UnmatchedPaymentEvent } from "@/shared/contracts";
import { apiFetch, swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/web/components/ui/dialog";
import { Input } from "@/web/components/ui/input";
import { Loading } from "@/web/components/ui/loading";
import { formatDate, formatMoney } from "@/web/lib/utils";

type UnmatchedResponse = { data: UnmatchedPaymentEvent[]; days: number };

export function UnmatchedPage() {
  const { data, isLoading, mutate } = useSWR<UnmatchedResponse>("/admin-api/unmatched?days=30", swrFetcher, {
    refreshInterval: 5_000,
  });
  const [selected, setSelected] = useState<UnmatchedPaymentEvent | null>(null);
  const [orderNo, setOrderNo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (isLoading || !data) return <Loading label="正在读取未匹配流水" />;

  async function submitMatch() {
    if (!selected || !orderNo.trim()) return;
    setSubmitting(true);
    try {
      const result = await apiFetch<{ out_trade_no: string; job_id: string }>(
        `/admin-api/unmatched/${encodeURIComponent(selected.account_log_id)}/match`,
        { method: "POST", body: JSON.stringify({ out_trade_no: orderNo.trim() }) },
      );
      toast.success(`已匹配订单 ${result.out_trade_no}，商户通知已入队`);
      setSelected(null);
      setOrderNo("");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "匹配失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function dismiss(event: UnmatchedPaymentEvent) {
    if (!window.confirm("确认这笔流水已经人工补款？标记后不会入账、不会通知。")) return;
    try {
      await apiFetch(`/admin-api/unmatched/${encodeURIComponent(event.account_log_id)}/dismiss`, { method: "POST" });
      toast.success("已标记为人工处理");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "标记失败");
    }
  }

  return (
    <>
      <PageHeader
        title="未匹配流水"
        description="支付宝已收款、但金额或时间没有匹配到订单的流水。核对商户订单号后可手工入账并补发一次通知。"
        actions={<Button variant="outline" onClick={() => mutate()}><RefreshCw />刷新</Button>}
      />

      <div className="mb-6 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <AlertTriangle className="mt-0.5 size-5 shrink-0" />
        <div>
          <p className="font-semibold">手工匹配会按订单原始金额入账</p>
          <p>只匹配金额等于订单原始金额或应付金额、且流水时间在订单确认窗口内的记录。匹配成功后会自动补发一次商户通知。</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>近 {data.days} 天未匹配收款</CardTitle>
          <CardDescription>每 5 秒刷新。匹配前请先在订单页确认该用户的商户订单号。</CardDescription>
        </CardHeader>
        <CardContent>
          {data.data.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-sm text-muted">
              <CheckCircle2 className="size-8 text-success" />
              当前没有未匹配流水
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b text-xs text-muted">
                  <tr>
                    <th className="pb-3 font-medium">到账时间</th>
                    <th className="pb-3 font-medium">金额</th>
                    <th className="pb-3 font-medium">支付宝订单号</th>
                    <th className="pb-3 font-medium">备注</th>
                    <th className="pb-3 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.data.map((event) => (
                    <tr key={event.account_log_id}>
                      <td className="py-3 text-xs text-muted">{formatDate(event.occurred_at)}</td>
                      <td className="py-3 font-mono font-semibold">{formatMoney(event.amount_cents)}</td>
                      <td className="max-w-56 truncate py-3 font-mono text-xs">{event.alipay_order_no || "—"}</td>
                      <td className="max-w-48 truncate py-3 text-xs text-muted">{event.trans_memo || "—"}</td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => dismiss(event)}>标记已处理</Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelected(event); setOrderNo(""); }}>
                            <Link2 />匹配订单
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => { if (!open) { setSelected(null); setOrderNo(""); } }}>
        <DialogContent>
          <DialogTitle>匹配未到账流水</DialogTitle>
          <DialogDescription>
            流水金额 {selected ? formatMoney(selected.amount_cents) : ""}，到账时间 {selected ? formatDate(selected.occurred_at) : ""}。
            请填写该用户的商户订单号（订单页里的 out_trade_no）。
          </DialogDescription>
          <div className="mt-4 space-y-3">
            <Input
              value={orderNo}
              onChange={(event) => setOrderNo(event.target.value)}
              placeholder="例如 USR159NO..."
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setSelected(null); setOrderNo(""); }}>取消</Button>
              <Button onClick={submitMatch} disabled={submitting || !orderNo.trim()}>
                {submitting ? "匹配中..." : "确认匹配并补发通知"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
